import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, RefreshCw, CheckCircle, XCircle, Calendar, Mail, Bell, Timer, AlertTriangle, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchedulerAdmin() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      } else if (res.status === 403) {
        toast.error('Accesso non autorizzato');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  }, [token, navigate]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/scheduler/logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching scheduler logs:', error);
    }
  }, [token]);

  const fetchEvents = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/events?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [token]);

  useEffect(() => {
    if (!user?.is_super_admin) {
      toast.error('Accesso riservato ai super admin');
      navigate('/dashboard');
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchLogs(), fetchEvents()]);
      setLoading(false);
    };
    
    loadData();
  }, [user, navigate, fetchStatus, fetchLogs, fetchEvents]);

  const runJob = async (jobName) => {
    setRunningJob(jobName);
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/scheduler/run-job/${jobName}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Job '${jobName}' eseguito in ${data.duration_seconds.toFixed(3)}s`);
        await Promise.all([fetchStatus(), fetchLogs()]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore esecuzione job');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setRunningJob(null);
    }
  };

  const refresh = async () => {
    await Promise.all([fetchStatus(), fetchLogs(), fetchEvents()]);
    toast.success('Dati aggiornati');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getJobIcon = (jobId) => {
    switch (jobId) {
      case 'daily_notifications':
        return <Mail className="h-4 w-4" />;
      case 'trial_expiring':
        return <AlertTriangle className="h-4 w-4" />;
      case 'plan_expired':
        return <XCircle className="h-4 w-4" />;
      case 'retention_job':
        return <FileText className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getJobDescription = (jobId) => {
    switch (jobId) {
      case 'daily_notifications':
        return 'Promemoria pagamenti, solleciti morosità, promemoria assemblee';
      case 'trial_expiring':
        return 'Avvisi scadenza trial (3 giorni prima)';
      case 'plan_expired':
        return 'Notifiche piano scaduto';
      case 'retention_job':
        return 'Gestione retention documenti GDPR';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Scheduler"
        subtitle="Attività pianificate"
        actions={
          <Button onClick={refresh} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        }
      />

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Stato Scheduler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {status?.scheduler_running ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-lg font-semibold text-green-600">Attivo</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-lg font-semibold text-red-600">Inattivo</span>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Timezone: {status?.timezone}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Job Configurati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{status?.jobs?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              APScheduler {status?.scheduler_enabled ? 'abilitato' : 'disabilitato'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Esecuzioni Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logs?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              Ultime 50 esecuzioni
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="jobs">
        <TabsList>
          <TabsTrigger value="jobs">Job Programmati</TabsTrigger>
          <TabsTrigger value="logs">Log Esecuzioni</TabsTrigger>
          <TabsTrigger value="events">Eventi Notifica</TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Job Schedulati</CardTitle>
              <CardDescription>
                Esecuzione automatica secondo schedule configurato
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Prossima Esecuzione</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {status?.jobs?.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getJobIcon(job.id)}
                          <span className="font-medium">{job.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs">
                        {getJobDescription(job.id)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <Timer className="h-3 w-3 mr-1" />
                          {job.trigger}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {formatDate(job.next_run)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runJob(job.id)}
                          disabled={runningJob !== null}
                        >
                          {runningJob === job.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                          <span className="ml-1">Esegui Ora</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Manual Jobs */}
                  <TableRow>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span className="font-medium">Retention Job</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs">
                      Gestione retention documenti GDPR
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">Manuale</Badge>
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runJob('retention_job')}
                        disabled={runningJob !== null}
                      >
                        {runningJob === 'retention_job' ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        <span className="ml-1">Esegui Ora</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Log Esecuzioni</CardTitle>
              <CardDescription>
                Storico esecuzioni job (manuali e automatiche)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nessun log disponibile
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Durata</TableHead>
                      <TableHead>Avviato da</TableHead>
                      <TableHead>Data/Ora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">{log.job_name}</TableCell>
                        <TableCell>
                          {log.status === 'SUCCESS' ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Successo
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="h-3 w-3 mr-1" />
                              Errore
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.duration_seconds?.toFixed(3)}s
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.triggered_by_email || 'Sistema'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(log.started_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle>Eventi Notifica</CardTitle>
              <CardDescription>
                Eventi processati dai job (per evitare duplicati)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nessun evento registrato
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Condominio</TableHead>
                      <TableHead>Processato</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Badge variant="outline">{event.key}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{event.workspace_id?.slice(0, 8)}...</TableCell>
                        <TableCell className="text-sm">
                          {event.condominium_id?.slice(0, 8) || '-'}...
                        </TableCell>
                        <TableCell>
                          {event.processed ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(event.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
